import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authApi, schoolApi } from '../../api/client';
import { FaEnvelope, FaLock, FaSchool, FaEye, FaEyeSlash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import Spinner from '../../components/common/Spinner';

export default function Login() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const { login }  = useAuth();

  const [school,   setSchool]   = useState(null);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading,  setLoading]  = useState(false);
  const [fetching, setFetching] = useState(true);
  const isSuperAdminRoute = !slug || window.location.pathname.includes('/super-admin/login');
  const isParentRoute     = window.location.pathname === '/parent/login';
  const [isSuperAdmin, setIsSuperAdmin] = useState(isSuperAdminRoute);

  useEffect(() => {
    if (!slug || isParentRoute) { setFetching(false); if (isSuperAdminRoute) setIsSuperAdmin(true); return; }
    schoolApi.getInfo(slug)
      .then(r => setSchool(r.data.data))
      .catch(() => navigate('/'))
      .finally(() => setFetching(false));
  }, [slug, navigate]);

  const primary = school?.primary_color || '#0d6efd';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Please fill all fields');
    setLoading(true);
    try {
      let data;

      if (isParentRoute) {
        // Parent platform login
        const res = await authApi.parentLogin({ email, password });
        data = res.data;
        if (!data.success) throw new Error(data.message);
        login(data.data.user, null, data.data.access_token, data.data.refresh_token);
        // Store children list for parent dashboard
        localStorage.setItem('parent_children', JSON.stringify(data.data.children || []));
        toast.success('Welcome back!');
        navigate('/parent');
      } else {
        // School user / super admin login
        const payload = { email, password };
        if (!isSuperAdmin && slug) payload.school_slug = slug;
        const res = await authApi.login(payload);
        data = res.data;
        if (!data.success) throw new Error(data.message);
        login(data.data.user, data.data.school || null, data.data.access_token, data.data.refresh_token);
        toast.success('Welcome back!');
        const role = data.data.user.role;
        if (data.data.user.type === 'platform' || role === 'super_admin') navigate('/super-admin');
        else if (role === 'admin')   navigate('/admin');
        else if (role === 'teacher') navigate('/teacher');
        else if (role === 'learner') navigate('/learner');
        else if (role === 'parent')  navigate('/parent');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <div className="login-page"><Spinner /></div>;

  return (
    <div className="login-page" style={school ? { background: `linear-gradient(135deg, #1a1a2e, ${primary}55)` } : {}}>
      <div className="login-card">
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          {school?.logo
            ? <img src={school.logo} alt="logo" style={{ width: 70, height: 70, borderRadius: 14, objectFit: 'cover', marginBottom: 12 }} />
            : <div style={{ width: 70, height: 70, borderRadius: 14, background: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                <FaSchool size={30} color="#fff" />
              </div>
          }
          <h2 style={{ fontWeight: 700, fontSize: '1.4rem', color: '#111827' }}>{school?.name || 'School SMS'}</h2>
          <p style={{ color: '#6b7280', fontSize: '0.85rem', marginTop: 4 }}>
            {isParentRoute ? 'Parent Portal Login' : isSuperAdmin ? 'Super Admin Login' : 'Sign in to your account'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label className="sms-label">Email Address</label>
            <div style={{ position: 'relative' }}>
              <FaEnvelope style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input className="sms-input" style={{ paddingLeft: 38 }} type="email" placeholder="your@email.com"
                value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
          </div>

          <div style={{ marginBottom: 24 }}>
            <label className="sms-label">Password</label>
            <div style={{ position: 'relative' }}>
              <FaLock style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input className="sms-input" style={{ paddingLeft: 38, paddingRight: 38 }}
                type={showPass ? 'text' : 'password'} placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} required />
              <button type="button" onClick={() => setShowPass(!showPass)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}>
                {showPass ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <button type="submit" disabled={loading} className="sms-btn sms-btn-primary" style={{ width: '100%', justifyContent: 'center', background: primary, fontSize: '0.95rem', padding: '11px' }}>
            {loading ? <Spinner size={20} color="#fff" /> : 'Sign In'}
          </button>

          {/* Forgot password */}
          <div style={{ textAlign: 'center', marginTop: 14 }}>
            <button
              type="button"
              onClick={() => {
                if (slug) navigate(`/school/${slug}/forgot-password`);
                else if (isParentRoute) navigate('/parent/forgot-password');
                else navigate('/super-admin/forgot-password');
              }}
              style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', fontSize: '0.83rem', textDecoration: 'underline' }}>
              Forgot your password?
            </button>
          </div>
        </form>

        {slug && !isSuperAdminRoute && (
          <div style={{ textAlign: 'center', marginTop: 20 }}>
            <button onClick={() => navigate(`/school/${slug}`)} style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '0.83rem' }}>
              ← Back to school page
            </button>
          </div>
        )}

        {/* Super admin quick access */}
        {!isSuperAdmin && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <button onClick={() => { setIsSuperAdmin(true); setEmail(''); setPassword(''); }}
              style={{ background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', fontSize: '0.78rem' }}>
              Super Admin?
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
